import { Injectable, OnDestroy } from "@angular/core";
// global-services
import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { DashboardItemType } from "app/bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "app/golden-layout/golden-layout-addition-mode";
import {
  GLPopoutConfiguration,
  GoldenLayoutService,
} from "app/golden-layout/golden-layout-service";
import { Subject, Subscription } from "rxjs";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// services
import {
  ActiveUser,
  ActiveUserProfilePromiseService,
  OrderDataPrimaryLookupService,
  ProfilePromiseActionService,
  ProfileSettingKeys,
} from "shared/cubiq-services";
import { BfxUserProfileService } from "../../bfx-common/services/bfx-user-profile.service";
import { ATDLParameterDataModel } from "../data-atdl/model/atdl-parameter-data.model";
import { ICDNAuxMode } from "../data-idc/enums/icd-aux-mode.enum";
// order-entry enums
import { OENCommand } from "../data/enums/oe-command.enum";
import { OENDialogType } from "../data/enums/oe-dialog-type.enum";
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";
import { OENExtendedLoggingType } from "../data/enums/oe-extended-logging-type.enum";
import { OENLoadingOutcome } from "../data/enums/oe-loading-outcome.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";
import { OEDataModel } from "../data/models/oe-data.model";
import { OEInputModel } from "../data/models/oe-input.model";
// order-entry models
import { OELoadingResponseModel } from "../data/models/oe-loading-response.model";
import { ICGoodFromUntilModel } from "../interface/controls/models/bfx-good-from-until.model";
// interface-components models
import { OECommandEventModel } from "app/order-entry/data/models/oe-command-event.model";
import { OEAPIChannelService } from "app/order-entry/services/oe-api-channel.service";
import {
  ICAutocompleteGenericModel,
  ICFixedCcyModel,
  ICFixingSourceModel,
  ICMarkupModel,
  ICSuspendModel,
  ICTenorValueDateModel,
} from "../interface/controls/models/bfx-interface-controls.models";
import { ATDLDatasetService } from "./atdl/atdl-dataset.service";
import { ATDLProcessorService } from "./atdl/atdl-processor.service";
import { OEDatasetService } from "./basic/oe-dataset.service";
import { OEDialogService } from "./basic/oe-dialogs.service";
import { OELogSettingsService } from "./basic/oe-log-settings.service";
import { OELoggerService } from "./basic/oe-logger.service";
import { OELookupService } from "./basic/oe-lookup.service";
import { OEPermissionsService } from "./basic/oe-permissions.service";
import { OERateSubscriptionManagerService } from "./basic/oe-rate-subscription-manager.service";
import { OEScreenRegistryService } from "./basic/oe-screen-registry.service";
import { OESettingsService } from "./basic/oe-settings.service";
import { OEUtilityService } from "./basic/oe-utility.service";
import { OEModelGetterService } from "./model/oe-model-getter.service";
import { OEModelLockingService } from "./model/oe-model-locking.service";
import { OEModelOperationsService } from "./model/oe-model-operations.service";
import { OEModelSetterService } from "./model/oe-model-setter.service";
import { OEActionsService } from "./oe-actions.service";
import { OECloserService } from "./oe-closer.service";
import { OECoordinatorService } from "./oe-coordinator.service";
import { OELoaderService } from "./oe-loader.service";
import { OEValidatorService } from "./oe-validator.service";
import { OEWorkerActionsService } from "./worker/oe-worker-actions.service";
import { OENInputControl } from "app/order-entry/data/enums/oe-input-control.enum";
import { ConstraintValue } from "../../../shared/cubiq-services/model/core/ConstraintValue";
const logger = LoggerService.for(__dirname);

////

@Injectable()
export class OEApiService implements OnDestroy {
  /**********************************************************************/
  /* DATA */

  private clientPanelActiveTabs: any = {
    clientActiveTab: null,
    accountActiveTab: null,
  };

  private apiSubscription: Subscription;

  /** Set of currently active orders being refreshed */
  private activeOrderRefreshMap: Set<string> = new Set();

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    protected oeLogger: OELoggerService,
    private getter: OEModelGetterService,
    private setter: OEModelSetterService,
    private operations: OEModelOperationsService,
    private utility: OEUtilityService,
    private closer: OECloserService,
    private coordinator: OECoordinatorService,
    private loader: OELoaderService,
    private workerActions: OEWorkerActionsService,
    private permissions: OEPermissionsService,
    private settings: OESettingsService,
    private logSettings: OELogSettingsService,
    private rateManager: OERateSubscriptionManagerService,
    private actions: OEActionsService,
    private dialogs: OEDialogService,
    private dataset: OEDatasetService,
    private lookup: OELookupService,
    private validator: OEValidatorService,
    private atdlProcessor: ATDLProcessorService, // keep it here for atdl initialization
    private atdlDataset: ATDLDatasetService,
    private screenRegistry: OEScreenRegistryService,
    private locker: OEModelLockingService,
    private orderLookup: OrderDataPrimaryLookupService,
    //
    private _activeUserService: ActiveUserProfilePromiseService,
    protected translationService: TranslateService,
    private _profileService: ProfilePromiseActionService,
    private userProfileService: BfxUserProfileService,
    private layoutService: GoldenLayoutService,
    private channelService: OEAPIChannelService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
    this.initialize();
  }
  /**********************************************************************/
  /* INITIALIZATION */
  private initialize() {
    this.apiSubscription = this.channelService.oeApiMessage$.subscribe(
      ($event: OECommandEventModel) => {
        const refId = $event.data.refId;
        const orderId = $event.data.orderId;
        this.doOrderTicketRefresh(orderId, refId);
      },
    );
  }

  ngOnDestroy(): void {
    if (this.apiSubscription != null) {
      this.apiSubscription.unsubscribe();
      this.apiSubscription = null;
    }
  }
  /* ******************************************** */
  /* Public API - Load order entry defaults */

  /*public updateRungsBelow(refId: string, value: number): void {
		this.setter.setRungsBelow(refId,value);
		this.updateStrategy(refId);
	}*/

  public loadUserDefaults(refId: string): void {
    this.loader.loadUserDefaults(refId);
  }

  public loadOrderEntryModelForOrderId(
    orderId: string,
    mode: OENOperationMode,
  ): Promise<OELoadingResponseModel> {
    return this.actions.load(orderId, mode);
  }

  public cmdFreezeInterfaceControls(refId: string): void {
    this.coordinator.cmdFreezeInterfaceControls(refId);
  }

  public cmdUnfreezeInterfaceControls(refId: string): void {
    this.coordinator.cmdUnfreezeInterfaceControls(refId);
  }

  public getSpotFwdPairsDataset(): Array<string> {
    return this.dataset.ccyPairs.datasetSpotFwd;
  }

  public getCcyPairsDataset(): Array<string> {
    return this.dataset.ccyPairs.datasetAll;
  }

  public getNdfCcyPairsDataset(): Array<string> {
    return this.dataset.ccyPairs.datasetNdf;
  }

  public getBmarkCcyPairsDataset(): Array<string> {
    return this.dataset.ccyPairs.datasetBmark;
  }

  public getAlgoCcyPairsDataset(): Array<string> {
    return this.dataset.ccyPairs.datasetAlgo;
  }

  public getNDFAlgoCcyPairsDataset(): Array<string> {
    return this.dataset.ccyPairs.datasetNDFAlgo;
  }

  public getGammaCcyPairsDataset(): Array<string> {
    return this.dataset.ccyPairs.datasetGamma;
  }

  public getSpotFwdMKTCcyPairsDataset(): Array<string> {
    return this.dataset.ccyPairs.datasetSpotFwdMKT;
  }

  public actionIssueCloseOrderEntryCmd(refId: string): void {
    this.closer.actionIssueCloseOrderEntryCmd(refId);
  }

  public actionLoadStrategy(refId: string): void {
    this.loader.actionLoadStrategy(refId);
  }

  public actionDestroyOnClose(refId: string): void {
    this.closer.actionDestroyOnClose(refId);
  }

  public actionExtendedDestroyOnClose(refId: string): void {
    this.closer.performCloseAndDestroy(refId);
  }

  public actionAbortModifyWithDialog(refId: string): boolean {
    return this.dialogs.abortModify(refId);
  }

  public actionCloseNewOrderEntryWithDialog(refId: string): boolean {
    return this.dialogs.closeNewEntry(refId);
  }

  public actionAbortAmendMarkupWithDialog(refId: string): boolean {
    return this.dialogs.abortAmendMarkup(refId);
  }

  /**
   *  Initialise loading operations of data into their respective controls
   * @param refId
   * @param applyEventUpdateSync
   * @param appliedDisplayState
   */
  public actionLoadOrderOnInit(
    refId: string,
    applyEventUpdateSync?: boolean,
    appliedDisplayState?: OENDisplayState,
  ): void {
    this.loader.actionLoadOrderOnInit(refId, applyEventUpdateSync, appliedDisplayState);
  }

  public getAlgoLimitPriceMode(algoId: string | ICAutocompleteGenericModel): ICDNAuxMode {
    if (algoId instanceof ICAutocompleteGenericModel) {
      algoId = <string>algoId.value;
    }
    return this.atdlDataset.getLimitPriceMode(algoId);
  }
  /* ******************************************** */
  /* Public API - Dataset Loaders */

  public loadOrderTypeDatasetForOrder(
    refId: string = null,
    excludeMKT: boolean = true,
    excludeTSL: boolean = true,
  ): Array<ICAutocompleteGenericModel> {
    excludeTSL =
      excludeTSL ||
      (refId != null && this.getter.hasStrategy(refId) ? this.getter.hasSiblingLoop(refId) : false);
    if (this.dataset.control.orderTypeDatasetInitialized) {
      if (this.getter.isGammaStrategy(refId)) {
        return this.dataset.orderType.datasetGamma;
      } else if (excludeTSL && excludeMKT) {
        return this.dataset.orderType.datasetWithoutTSL_MKT;
      } else if (!excludeTSL && excludeMKT) {
        return this.dataset.orderType.datasetWithoutMKT;
      } else if (excludeTSL && !excludeMKT) {
        return this.dataset.orderType.datasetWithoutTSL;
      } else {
        return this.dataset.orderType.dataset;
      }
    } else {
      logger.out.error(
        `ERROR: oeAPI.loadOrderTypeDataset(): Order Type dataset not initialized or empty. Initialization flag: ${
          this.dataset.control.orderTypeDatasetInitialized ? "TRUE" : "FALSE"
        } `,
      );
    }
  }

  /**
   * Get list of all tradable algo types - if modifyId is specified, then that modify Algo Id is also included in the dataset
   * regardless of whether the algo is tradable - this allows a user to see the modify algo type in the dropdown.
   * @param modifyId
   * @returns
   */
  public loadTradableAlgos(modifyId?: string): Array<ICAutocompleteGenericModel> {
    const allAlgos = this.atdlDataset.getAllAlgosForAutoComplete();
    let tradableAlgos = this.settings.getTradableAlgos();
    if (!tradableAlgos) {
      tradableAlgos = new Set();
    }
    if (typeof modifyId === "string") {
      tradableAlgos = new Set(tradableAlgos).add(modifyId);
    }
    return allAlgos.filter((value) => tradableAlgos.has(value.value));
  }

  public loadFixingSourcesDataset(): Array<ICFixingSourceModel> {
    return this.dataset.fixingSources.dataset;
  }

  public loadOrderTypeDataset(excludeTSL: boolean = false): Array<ICAutocompleteGenericModel> {
    if (excludeTSL) {
      return this.dataset.orderType.datasetWithoutTSL;
    } else {
      return this.dataset.orderType.dataset;
    }
  }

  public loadGammaOrderTypeDataset(): Array<ICAutocompleteGenericModel> {
    return this.dataset.orderType.datasetGamma;
  }

  /* ******************************************** */
  /* PANEL SETTINGS */

  public getSystemConfigForMorePanel(): any {
    return this.settings.getSysConfigForMorePanel();
  }
  public getSystemConfigForFinancialsPanel(): any {
    return this.settings.getSysConfigForFinancialsPanel();
  }

  public getSystemConfigForClientPanel(): any {
    return this.settings.getSysConfigForClientPanel();
  }

  public getSystemConfigPermissions(): any {
    return this.permissions.getSysConfigPermissions();
  }

  public getPermissionEnterOrder(refId: string): boolean {
    if (this.isOperationModeNew(refId) || this.isOperationModeDuplicate(refId)) {
      return this.getSystemConfigPermissions().enterNewOrder;
    } else {
      return true;
    }
  }

  /* ******************************************** */
  /* Public API - Model UPDATE */

  /**
   * CLIENT - PANEL
   */
  public updateClient(refId: string, _clientData: CubiqTS.ClientImpl | null): void {
    try {
      this.operations.updateClient(refId, _clientData);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateClient(): exception:  %O", ex.message);
    }
  }

  public updateAccount(refId: string, _accountData: CubiqTS.AccountImpl): void {
    try {
      this.setter.setAccount(refId, _accountData);
      //this.coordinator.cmdPropagateAccountChange(refId);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.updateAccount(): exception:  %O", ex.message);
    }
  }

  public updateAllocations(refId: string, _allocations): void {
    try {
      this.setter.setAllocations(refId, _allocations);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.updateAllocations(): exception:  %O", ex.message);
    }
  }

  public updateFarAllocations(refId: string, _farAllocations): void {
    try {
      this.setter.setFarAllocations(refId, _farAllocations);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.setFarAllocations(): exception:  %O", ex.message);
    }
  }

  public updateBulked(refId: string, bulked): void {
    try {
      this.setter.setBulked(refId, bulked);
      if (bulked) {
        this.coordinator.cmdBulked(refId, OENInputControl.ALLOCATIONS);
      } else {
        this.coordinator.cmdUnbulk(refId, OENInputControl.ALLOCATIONS);
      }
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.updateBulked(): exception:  %O", ex.message);
    }
  }

  public updateBulking(refId: string, bulking): void {
    try {
      this.setter.setBulking(refId, bulking);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.updateBulking(): exception:  %O", ex.message);
    }
  }

  public updateProduct(refId: string, _product): void {
    try {
      this.setter.setProduct(refId, _product);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.setProduct(): exception:  %O", ex.message);
    }
  }

  public updateSwapFixedAmount(refId: string, _swapFixedAmount): void {
    try {
      this.setter.setSwapFixedAmount(refId, _swapFixedAmount);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.setProduct(): exception:  %O", ex.message);
    }
  }

  public updateTenor(refId: string, _tenor): void {
    try {
      this.setter.setTenor(refId, _tenor);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.setProduct(): exception:  %O", ex.message);
    }
  }

  public updateSwapTenor(refId: string, _swapTenor): void {
    try {
      this.setter.setSwapTenor(refId, _swapTenor);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.setProduct(): exception:  %O", ex.message);
    }
  }

  public updateSwapValueDate(refId: string, _swapValueDate): void {
    try {
      this.setter.setSwapValueDate(refId, _swapValueDate);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.setProduct(): exception:  %O", ex.message);
    }
  }

  public updateClientTrader(
    refId: string,
    _clientTrader: ICAutocompleteGenericModel | CubiqTS.UserImpl,
  ): void {
    try {
      let inputData = this.operations.updateClientTrader(refId, _clientTrader);
      if (inputData instanceof OEInputModel) {
        this.loader.propagateInputValueThroughLoad(refId, inputData);
      }
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateClientTrader(): exception:  %O", ex.message);
    }
  }

  public updateContacts(refId: string, _contacts: any): void {
    try {
      this.setter.setContacts(refId, _contacts);
      //	this.coordinator.cmdPropagateClientTraderChange(refId);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateContacts(): exception:  %O", ex.message);
    }
  }

  public updateClientDefaultComments(
    refId: string,
    _defaultClientComment: string,
    _defaultPrivateComment: string,
  ): void {
    try {
      this.setter.setDefaultComments(refId, _defaultClientComment, _defaultPrivateComment);

      // CHANGE	this.coordinator.cmdPropagateClientDefaultCommentsChange(refId);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateClientDefaultComments(): exception:  %O", ex.message);
    }
  }

  public updateClientTraderDefaultComment(
    refId: string,
    _defaultClientTraderComment: string,
  ): void {
    this.setter.setClientTraderClientComment(refId, _defaultClientTraderComment);
  }

  /**
   * FINANCIALS - PANEL
   */
  public updateCcyPair(
    refId: string,
    _ccyPair: string,
    _ccyPairModel: CubiqTS.CurrencyPairImpl,
    propagateToAllNodes: boolean = true,
    omitMKTNodes: boolean = false,
  ): void {
    try {
      if (propagateToAllNodes) {
        // MULTI NODE
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        if (omitMKTNodes && propagationTree.length > 1) {
          // update SPECIFIC nodes (avoid MKT nodes)
          propagationTree.forEach((itemRefId: string) => {
            let orderType = this.getter.get(itemRefId).orderType;
            let applyToNode: boolean =
              orderType == null ||
              (orderType instanceof ICAutocompleteGenericModel &&
                orderType.value !== CubiqTS.RequestOrderType.MARKET);

            if (applyToNode) {
              this.setter.setCcyPair(itemRefId, _ccyPair, _ccyPairModel, false);
              this.updateStrategy(refId);
            }
          });
        } else {
          // update ALL nodes (Default)
          this.setter.setCcyPair(refId, _ccyPair, _ccyPairModel, true);
          this.updateMKTStrategyLegsLabelsWithErrorsOnCcyPairChange(
            refId,
            _ccyPairModel.allowMarketOrdersFg,
          );
        }
      } else {
        // SINGLE NODE (MKT NODE)
        this.setter.setCcyPair(refId, _ccyPair, _ccyPairModel, false);
        this.updateStrategy(refId);
      }
    } catch (ex) {
      logger.out.error(`ERROR: oeAPI.updateCcyPair(): exception: ${ex}`);
    }
  }

  public updateMKTStrategyLegsLabelsWithErrorsOnCcyPairChange(
    refId: string,
    ccyPairMKTEnabled: boolean,
  ): void {
    if (
      this.getter.has(refId) &&
      this.getter.isRootNode(refId) &&
      this.getter.hasStrategy(refId) &&
      !ccyPairMKTEnabled
    ) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      for (let itemRefId of propagationTree) {
        if (this.getter.has(itemRefId)) {
          let orderType = this.getter.get(itemRefId).orderType;
          if (
            orderType == null ||
            (orderType instanceof ICAutocompleteGenericModel &&
              (orderType.value == null || orderType.value === CubiqTS.RequestOrderType.MARKET))
          ) {
            this.validator.validateOrderData(itemRefId);
            this.updateStrategy(itemRefId);
          }
        }
      }
    }
  }

  public updateOrderType(refId: string, _orderType: any): void {
    try {
      let reValidate =
        this.getter.get(refId).orderType == null && this.getter.hasValidationErrors(refId);

      this.setter.setOrderType(refId, _orderType);
      let modelOrderType = this.getter.get(refId).orderType;
      // Handle TSL
      if (
        !(
          this.getter.isOperationModeModifyInfo(refId) ||
          this.getter.isOperationModeAmendMarkup(refId) ||
          this.getter.isOperationModeMonitorExecution(refId)
        )
      ) {
        // dont update locking permission for MODIFY_INFO & Amend Markup
        if (
          modelOrderType != null &&
          (modelOrderType.value === CubiqTS.RequestOrderType.TSL ||
            modelOrderType.value === CubiqTS.RequestOrderType.TSL_ASK ||
            modelOrderType.value === CubiqTS.RequestOrderType.TSL_BID)
        ) {
          this.operations.updateRelationshipPermission(
            refId,
            CubiqTS.OrderRelationshipType.LOOP,
            OENLockedState.LOCKED,
          );
          //this.coordinator.cmdUpdateStrategyButtonsLocking(refId);
        } else if (
          this.getter.isNotInTerminalState(refId) &&
          !this.getter.hasSiblings(refId) &&
          !this.getter.hasChildren(refId)
        ) {
          this.operations.updateRelationshipPermission(
            refId,
            CubiqTS.OrderRelationshipType.LOOP,
            OENLockedState.UNLOCKED,
          );
          //this.coordinator.cmdUpdateStrategyButtonsLocking(refId);
        }
        this.updateActionsAndNotificationsPanelsForMKTOrder(refId);
      }

      // Re-Validation added to accommodate MKT leg reset on root ccy-pair change
      if (reValidate) {
        this.validator.validateOrderData(refId);
      }
      this.updateStrategy(refId);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateOrderType(): exception: %O", ex.message);
    }
  }

  public updateAlgoType(refId: string, _algoType: any): void {
    try {
      this.setter.setAlgoType(refId, _algoType);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateAlgoType(): exception: ", ex.message);
    }
  }

  public updateOrderAmount(refId: string, _orderAmount: CubiqTS.BigNumber): void {
    try {
      if (this.setter.setOrderAmount(refId, _orderAmount)) {
        this.updateStrategy(refId);
      }
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateOrderAmount(): exception:  %O", ex.message);
    }
  }

  public updateClientIntent(refId: string, _clientIntent: CubiqTS.OrderIntent): void {
    try {
      if (this.setter.setClientIntent(refId, _clientIntent)) {
        this.updateStrategy(refId);
        this.coordinator.cmdPropagateClientIntentChange(refId, _clientIntent);
      }
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateClientIntent(): exception:  %O", ex.message);
    }
  }

  public updateFixedCcy(
    refId: string,
    _fixedCcy: ICFixedCcyModel,
    propagateToAllNodes: boolean = true,
    omitMKTNodes: boolean = false,
  ): void {
    try {
      if (propagateToAllNodes) {
        // MULTI NODE
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        if (omitMKTNodes && propagationTree.length > 1) {
          // update SPECIFIC nodes (avoid MKT nodes)
          propagationTree.forEach((itemRefId: string) => {
            let orderType = this.getter.get(itemRefId).orderType;
            let applyToNode: boolean =
              orderType == null ||
              (orderType instanceof ICAutocompleteGenericModel &&
                orderType.value !== CubiqTS.RequestOrderType.MARKET);

            if (applyToNode) {
              this.setter.setFixedCcy(itemRefId, _fixedCcy, false);
            }
          });
        } else {
          // update ALL nodes (Default)
          this.setter.setFixedCcy(refId, _fixedCcy);
        }
      } else {
        // SINGLE NODE (MKT NODE)
        this.setter.setFixedCcy(refId, _fixedCcy, false);
      }
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateClientIntent(): exception:  %O", ex.message);
    }
  }

  // in spot
  public updateRequestPrice(refId: string, _requestPrice: CubiqTS.BigNumber): void {
    try {
      if (this.setter.setRequestPrice(refId, _requestPrice)) {
        this.updateStrategy(refId);
      }
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateRequestPrice(): exception:  %O", ex.message);
    }
  }

  public updateTrailingPips(refId: string, _trailingPips: CubiqTS.BigNumber): void {
    try {
      if (this.setter.setTrailingPips(refId, _trailingPips)) {
        this.updateStrategy(refId);
      }
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateRequestPrice(): exception:  %O", ex.message);
    }
  }

  public updateTrailingTrigger(refId: string, _trailingTrigger: CubiqTS.BigNumber): void {
    try {
      if (this.setter.setTrailingTrigger(refId, _trailingTrigger)) {
        this.updateStrategy(refId);
      }
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateRequestPrice(): exception:  %O", ex.message);
    }
  }

  // in algos
  public updateLimitPrice(refId: string, value: any): void {
    try {
      this.setter.setLimitPrice(refId, value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateLimitPrice(): exception:  %O", ex.message);
    }
  }

  // in bmark
  public updateFixing(
    refId: string,
    _fixCode: string,
    _fixTime: string,
    _fixTZ: string,
    forceUpdate = false,
  ): void {
    try {
      this.setter.setFixing(refId, _fixCode, _fixTime, _fixTZ, forceUpdate);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateFixing(): exception:  %O", ex.message);
    }
  }

  public updateValueDate(refId: string, _valueDate: ICTenorValueDateModel): void {
    try {
      this.setter.setValueDate(refId, _valueDate);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateValueDate(): exception:  %O", ex.message);
    }
  }

  public updateAllowPartialFills(refId: string, _allowPartialFills: ConstraintValue): void {
    try {
      this.setter.setAllowPartialFills(refId, _allowPartialFills);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.updateAllowPartialFills(): exception:  %O", ex.message);
    }
  }

  public updateAllowSTPAgg(refId: string, _allowSTPAgg: ConstraintValue): void {
    try {
      this.setter.setAllowSTPAgg(refId, _allowSTPAgg);
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.updateAllowSTPAgg(): exception:  %O", ex.message);
    }
  }

  public updateMarkup(refId: string, _markup: ICMarkupModel): void {
    try {
      this.setter.setMarkup(refId, _markup == null ? null : _markup);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateMarkup(): exception:  %O", ex.message);
    }
  }

  /**
   * MORE - PANEL
   */

  public updatePrivateComment(refId: string, _comment: string): void {
    try {
      this.setter.setPrivateComment(refId, _comment);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updatePrivateComment(): exception:  %O", ex.message);
    }
  }

  public updateClientComment(refId: string, _comment: string): void {
    try {
      this.setter.setManualClientComment(refId, _comment);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateClientComment(): exception:  %O", ex.message);
    }
  }

  public updateHoldSTP(refId: string, _value: boolean): void {
    try {
      this.setter.setHoldSTP(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateHoldSTP(): exception:  %O", ex.message);
    }
  }

  public updateAutoForwardPrice(refId: string, _value: boolean): void {
    try {
      this.setter.setAutoForwardPrice(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateHoldSTP(): exception:  %O", ex.message);
    }
  }

  public updatePrivateOrder(refId: string, _value: boolean): void {
    try {
      this.setter.setPrivateOrder(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updatePrivateOrder(): exception:  %O", ex.message);
    }
  }

  public updateExecutionMode(refId: string, _value: CubiqTS.ExecutionType): void {
    try {
      //if(!this.sync.isUpdateBlocked(refId) || this.isOperationModeNew(refId)){ // do not block on new
      if (this.setter.setExecutionMode(refId, _value)) {
        this.updateStrategy(refId, true);
      }
      /* 	this.coordinator.cmdPropagateExecutionModeChange(refId);
			}else if(_value != null){ // condition not needed but what the hell
				// added to fix: https://www.bcdfx.com/ijira/browse/OMS-26182
				// auto-execute is needed in order to properly load the traderGroup dataset on initialization for duplicate and modify
				this.coordinator.cmdPropagateExecutionModeChange(refId);
			} */
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateExecutionMode(): exception:  %O", ex.message);
    }
  }

  public updateTraderGroup(refId: string, _value: CubiqTS.TradingGroupImpl): void {
    try {
      this.setter.setTraderGroup(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateTraderGroup(): exception:  %O", ex.message);
    }
  }

  public updateTrigger(refId: string, _value: ICAutocompleteGenericModel): void {
    try {
      this.setter.setTrigger(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateTraderGroup(): exception:  %O", ex.message);
    }
  }

  public updateTrailingTriggerProvided(refId: string, _value: boolean): void {
    try {
      this.setter.setTrailingTriggerProvided(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateTraderGroup(): exception:  %O", ex.message);
    }
  }

  public updateSuspend(refId: string, _value: ICSuspendModel): void {
    try {
      this.setter.setSuspend(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateSuspend(): exception:  %O", ex.message);
    }
  }

  public updateGoodFrom(refId: string, _value: ICGoodFromUntilModel): void {
    try {
      this.setter.setGoodFrom(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateGoodFrom(): exception:  %O", ex.message);
    }
  }

  public updateGoodUntil(refId: string, _value: ICGoodFromUntilModel): void {
    try {
      this.setter.setGoodUntil(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateGoodUntil(): exception:  %O", ex.message);
    }
  }

  public updateFixingSource(refId: string, _value: ICFixingSourceModel): void {
    try {
      this.setter.setFixingSource(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateFixingCode(): exception:  %O", ex.message);
    }
  }

  public updateFixingDate(refId: string, _value: Date): void {
    try {
      this.setter.setFixingDate(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateFixingDate(): exception:  %O", ex.message);
    }
  }
  /**********************************************************************/
  /* Algo Parameters  */

  public setAlgoParameters(
    refId: string,
    _algoId: string,
    _algoParameters: Array<ATDLParameterDataModel>,
  ): void {
    try {
      this.setter.setAlgoParameters(refId, _algoId, _algoParameters);
    } catch (ex) {
      logger.out.error(`ERROR: oeAPI.updateAlgoParameters(): Exception: %O`, ex);
    }
  }

  public updateAlgoParameter(
    refId: string,
    _algoId: string,
    _algoParameter: ATDLParameterDataModel,
  ): void {
    try {
      this.setter.setAlgoParameter(refId, _algoId, _algoParameter);
    } catch (ex) {
      logger.out.error(`ERROR: oeAPI.updateAlgoParameter(): Exception: %O`, ex);
    }
  }

  public updateAlgoParameterValue(
    refId: string,
    _algoId: string,
    _algoParameterName: string,
    _algoParameterValue: any,
  ): void {
    try {
      this.setter.setAlgoParameterValue(refId, _algoId, _algoParameterName, _algoParameterValue);
    } catch (ex) {
      logger.out.error(`ERROR: oeAPI.updateAlgoParameterValue(): Exception: %O`, ex);
    }
  }

  public updateOBOClient(refId: string, _value: string): void {
    try {
      this.setter.setOBOClient(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateOBOClient(): exception:  %O", ex.message);
    }
  }

  public updateOBOAccount(refId: string, _value: string): void {
    try {
      this.setter.setOBOAccount(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateOBOAccount(): exception:  %O", ex.message);
    }
  }

  public updateOBOUser(refId: string, _value: string): void {
    try {
      this.setter.setOBOUser(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateOBOUser(): exception:  %O", ex.message);
    }
  }

  public updateDelta(refId: string, _value: string): void {
    try {
      this.setter.setDelta(refId, _value);
      this.updateStrategy(refId);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateDelta(): exception:  %O", ex.message);
    }
  }

  public updateRungsAbove(refId: string, _value: number): void {
    try {
      this.setter.setRungsAbove(refId, _value);
      this.updateStrategy(refId);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateRungsAbove(): exception:  %O", ex.message);
    }
  }

  public updateRungsBelow(refId: string, _value: number): void {
    try {
      this.setter.setRungsBelow(refId, _value);
      this.updateStrategy(refId);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateRungsBelow(): exception:  %O", ex.message);
    }
  }

  public updateCustomFields(refId: string, _value: Array<CubiqTS.CustomValue>): void {
    try {
      this.setter.setCustomValues(refId, _value);
    } catch (ex) {
      logger.out.error("ERROR: oeAPI.updateCustomFields(): exception:  %O", ex.message);
    }
  }

  /**********************************************************************/
  /* Actions API  */

  public actionSubmitOrder(refId: string): Promise<boolean> {
    return this.actions.submit(refId);
  }

  public actionSaveOrder(refId: string): Promise<boolean> {
    return this.actions.save(refId);
  }

  public actionCreateIfDone(parentRefId: string): void {
    this.actions.createIfDoneRelationship(parentRefId);
  }

  public actionCreateLoop(parentRefId: string): void {
    this.actions.createLoopRelationship(parentRefId);
  }

  public actionCreateOco(parentRefId: string): void {
    this.actions.createOcoRelationship(parentRefId);
  }

  public actionCreateTimeout(parentRefId: string): void {
    this.actions.createTimeoutRelationship(parentRefId);
  }

  public actionRemoveStrategy(refId: string): void {
    let rootRefId = this.getter.getRootRefIdForStrategyRefId(refId);
    this.actions.removeStrategy(refId);
    if (rootRefId != null) {
      // Update SAVE button availability, fix for OMS-36445
      this.updateActionsAndNotificationsPanelsForMKTOrder(rootRefId, false);
    }
  }

  public actionReset(refId: string): boolean {
    return this.actions.reset(refId);
  }

  public actionCreateOrder(entryType: OENEntryType): string {
    let refId: string = this.operations.addEmptyInstanceType(entryType);
    this.locker.createLockingProfileForNewEntry(refId);
    return refId;
  }

  public actionNewOrder(refId: string, entryType: OENEntryType): void {
    this.actions.newInstance(refId, entryType);
  }

  public actionEditOrder(
    activeRefId: string,
    setModeToNew: boolean = false,
    fromEdit: boolean = false,
    scratchpad: boolean = false,
  ): void {
    this.actions.edit(activeRefId, setModeToNew, fromEdit, scratchpad);
  }

  public actionReInitModify(activeRefId: string, operationMode: OENOperationMode): void {
    let orderId = this.getter.getOrderIdOfActiveStrategyLeg(activeRefId);
    this.doOrderEntryPopupForOrderId(orderId, operationMode);
  }

  public actionCopyOrder(refId: string, setModeToNew: boolean = true): void {
    this.actions.copy(refId, setModeToNew);
  }

  public actionDuplicateOrder(refId: string, setOperationModeToNew: boolean = true): void {
    this.actions.duplicate(this, refId, setOperationModeToNew);
  }

  public actionDiApprove(refId: string): Promise<CubiqTS.ErrorsImpl> {
    return this.workerActions.submitDIApprove(refId);
  }

  public actionDiReject(refId: string): Promise<CubiqTS.ErrorsImpl> {
    return this.workerActions.submitDIReject(refId);
  }

  public actionToleranceAccept(refId: string): void {
    this.workerActions.submitToleranceAccept(refId);
  }

  public actionNegativeMarkupAccept(refId: string): void {
    this.workerActions.submitNegativeMarkupAccept(refId);
  }

  public actionBmZeroAmountAccept(refId: string): void {
    this.workerActions.submitBmZeroAmountAccept(refId);
  }

  public actionModifyOutAccept(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    let isExternal =
      CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED === this.getter.get(refId).srvOrderState;

    return new Promise((res) => {
      if (isExternal) {
        // introduced for https://www.bcdfx.com/ijira/browse/OMS-32098
        this.dialogs.showDialog(
          refId,
          OENDialogType.ORDER_ENTRY_FORCE_EXT_ORDER,
          () => {
            // Call the server and get result as promise
            const serverResult = this.workerActions.submitModifyOutAccept(refId);
            // Wait until promise is resolved and then reduce to array of errors
            this.reduceServerErrors(serverResult).then((errors) => res(errors));
          },
          () => {
            this.abortExtAction(refId);
            res([]);
          },
        );
      } else {
        const serverResult = this.workerActions.submitModifyOutAccept(refId);
        this.reduceServerErrors(serverResult).then((errors) => res(errors));
      }
    });
  }

  public actionModifyOutReject(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    let isExternal =
      CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED === this.getter.get(refId).srvOrderState;

    return new Promise((res) => {
      if (isExternal) {
        // introduced for https://www.bcdfx.com/ijira/browse/OMS-32098
        this.dialogs.showDialog(
          refId,
          OENDialogType.ORDER_ENTRY_FORCE_EXT_ORDER,
          () => {
            // Call the server and get result as promise
            const serverResult = this.workerActions.submitModifyOutReject(refId);
            // Wait until promise is resolved and then reduce to array of errors
            this.reduceServerErrors(serverResult).then((errors) => res(errors));
          },
          () => {
            this.abortExtAction(refId);
            res([]);
          },
        );
      } else {
        const serverResult = this.workerActions.submitModifyOutReject(refId);
        this.reduceServerErrors(serverResult).then((errors) => res(errors));
      }
    });
  }

  public actionTrAccept(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    let isExternal =
      CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED === this.getter.get(refId).srvOrderState;

    return new Promise((res) => {
      if (isExternal) {
        // introduced for https://www.bcdfx.com/ijira/browse/OMS-32098
        this.dialogs.showDialog(
          refId,
          OENDialogType.ORDER_ENTRY_FORCE_EXT_ORDER,
          () => {
            // Call the server and get result as promise
            const serverResult = this.workerActions.submitTRAccept(refId);
            // Wait until promise is resolved and then reduce to array of errors
            this.reduceServerErrors(serverResult).then((errors) => res(errors));
          },
          () => {
            this.abortExtAction(refId);
            res([]);
          },
        );
      } else {
        const serverResult = this.workerActions.submitTRAccept(refId);
        this.reduceServerErrors(serverResult).then((errors) => res(errors));
      }
    });
  }

  public actionTrAcceptTop(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    return this.workerActions.submitTRAcceptTopside(refId);
  }

  public actionTrAcceptDown(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    return this.workerActions.submitTRAcceptDownside(refId);
  }

  public actionTrReject(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    let isExternal =
      CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED === this.getter.get(refId).srvOrderState;

    return new Promise((res) => {
      if (isExternal) {
        // introduced for https://www.bcdfx.com/ijira/browse/OMS-32098
        this.dialogs.showDialog(
          refId,
          OENDialogType.ORDER_ENTRY_FORCE_EXT_ORDER,
          () => {
            // Call the server and get result as promise
            const serverResult = this.workerActions.submitTRReject(refId);
            // Wait until promise is resolved and then reduce to array of errors
            this.reduceServerErrors(serverResult).then((errors) => res(errors));
          },
          () => {
            this.abortExtAction(refId);
            res([]);
          },
        );
      } else {
        const serverResult = this.workerActions.submitTRReject(refId);
        this.reduceServerErrors(serverResult).then((errors) => res(errors));
      }
    });
  }

  private abortExtAction(refId: string): void {
    let activeRefId = this.getter.getActivelyDisplayedRefId(refId);
    activeRefId = activeRefId != null ? activeRefId : refId;
    this.coordinator.cmdChangeDisplayState(activeRefId, OENCommand.STATE_REVAL_BUTTONS);
  }

  public actionLockAll(refId: string): void {
    if (this.getter.has(refId)) {
      this.coordinator.cmdLockInterfaceControls(refId);
    }
  }

  public actionUnlockAll(refId: string): void {
    if (this.getter.has(refId)) {
      this.coordinator.cmdUnlockInterfaceControls(refId);
    }
  }

  public actionAbort(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    return this.actions.abort(refId);
  }

  /**
   * Abort current operation but continue monitoring specified order. Windows
   * will not be closed and order data will be refreshed
   * @param refId
   */
  public actionAbortToMonitoring(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    if (this.getter.has(refId)) {
      if (
        this.getter.isOperationModeModifyInfo(refId) &&
        this.getter.supportsMonitoringExecution(refId)
      ) {
        // No need to do abort but we need to reload from latest orderData
        this.setter.setOperationModeToMonitorExecution(refId, true);
        this.coordinator.cmdChangeDisplayState(refId, OENCommand.STATE_MONITOR_EXECUTION);
        // this.coordinator.cmdLockInterfaceControls(refId, false);
        const orderId = this.getter.getOrderId(refId);
        if (this.getter.isRootNodeAlone(refId)) {
          // If there's just one order, then we retrieve data from the active order service
          const order = this.orderLookup.getBase().getItem(orderId);
          if (order instanceof CubiqTS.OrderDataImpl) {
            this.operations.processExternalOrderUpdate(order);
          }
        } else {
          // complex strategy - all data could have been overridden so need tro reload everything
          this.doOrderTicketRefresh(orderId, refId);
        }
        return Promise.resolve([]);
      } else {
        return this.actions.abort(refId);
      }
    }
  }
  // returns true if validation errors, false otherwise
  public actionAmApprove(refId: string): boolean {
    return this.workerActions.submitAmendMarkupRequest(refId);
  }

  public actionAmReject(refId: string): void {
    this.workerActions.submitDIReject(refId);
  }

  public updateModelReference(oldReference: string, newReference: string): void {
    this.actions.updateModelReference(oldReference, newReference);
  }

  /**********************************************************************/
  /* ORDER-ENTRY MODEL - ACCESS API */

  public isRoot(refId: string): boolean {
    return this.getter.isRootNode(refId);
  }

  public isBulking(refId: string): boolean {
    return this.getter.isBulking(refId);
  }

  public getModifyIssuerRefId(contextRefId: string): string {
    return this.getter.getModifyIssuerRefId(contextRefId);
  }

  public has(refId: string): boolean {
    return this.getter.has(refId);
  }

  public isOperationModeModify(refId: string): boolean {
    return this.getter.isOperationModeModify(refId);
  }

  public isOperationModeModifyInfo(refId: string): boolean {
    return this.getter.isOperationModeModifyInfo(refId);
  }

  public isOperationModeNew(refId: string): boolean {
    return this.getter.isOperationModeNew(refId);
  }

  public isOperationModeDuplicate(refId: string): boolean {
    return this.getter.isOperationModeDuplicate(refId);
  }

  public isOperationModeAmendMarkup(refId: string): boolean {
    return this.getter.isOperationModeAmendMarkup(refId);
  }

  public isOperationModeEdit(refId: string): boolean {
    return this.getter.isOperationModeEdit(refId);
  }

  public isOperationModeMonitorExecution(refId: string): boolean {
    return this.getter.isOperationModeMonitorExecution(refId);
  }

  public isGammaCustomizeMode(refId: string): boolean {
    return this.getter.isGammaCustomizeMode(refId);
  }

  public getOperationMode(refId: string): OENOperationMode {
    return this.getter.getOperationMode(refId);
  }

  public getEntryType(refId: string): OENEntryType {
    return this.getter.getEntryType(refId);
  }

  public getFillState(refId: string): CubiqTS.FillState {
    return this.getter.getFillState(refId);
  }

  public hasBeenPartiallyFilled(refId: string): boolean {
    return this.getter.hasBeenPartiallyFilled(refId);
  }

  public isDisplayState_ShowOrderEntry(refId: string): boolean {
    if (this.has(refId)) {
      return this.getter.getDisplayState(refId) === OENDisplayState.SHOW_ORDER_ENTRY;
    }
    return false;
  }

  public getOrderIdForRefId(refId: string): string {
    return this.getter.getOrderId(refId);
  }

  public getOrderReferenceForRefId(refId: string): string {
    return this.getter.getOrderRef(refId);
  }

  public getClientComment(refId: string): string {
    return this.getter.getClientComment(refId);
  }

  public determineExternalTraderGroupOnEntry(refId: string): boolean {
    let traderGroup: any = this.getter.getTraderGroup(refId);
    if (traderGroup != null && traderGroup instanceof CubiqTS.TradingGroupImpl) {
      return traderGroup.isExternallyFilledFg;
    } else if (traderGroup != null && typeof traderGroup === "string") {
      return this.lookup.determineExternalTraderGroupNow(traderGroup);
    } else {
      return false;
    }
  }

  public determineExternalTraderGroupFromBaseLookup(refId: string): boolean {
    if (this.has(refId)) {
      let orderId: string = this.getter.getActionableOrderId(refId);
      return this.lookup.determineExternalTraderGroupFromBaseLookupNow(orderId);
    } else {
      // error
    }
    return null;
  }

  // OMS-36177
  public getClientBranchOEModelProperty(refId: string): string {
    if (this.getter.has(refId)) {
      return this.getter.get(refId).clientBranch;
    }
    return null;
  }

  public isModificationPrivatized(refId: string): boolean {
    return this.getter.has(refId) ? this.getter.get(refId).isModificationPrivatized : false;
  }

  public getRootCcyPair(refId: string): string {
    if (this.getter.hasStrategy(refId)) {
      let rootModel = this.getter.getRootModel(refId);
      if (rootModel instanceof OEDataModel) {
        return rootModel.ccyPair;
      }
    }
    return null;
  }

  public getRootFixedCcy(refId: string): ICFixedCcyModel {
    if (this.getter.hasStrategy(refId)) {
      let rootModel = this.getter.getRootModel(refId);
      if (rootModel instanceof OEDataModel) {
        let fixedCcyModel = rootModel.fixedCcy;
        return fixedCcyModel instanceof ICFixedCcyModel ? fixedCcyModel : null;
      }
    }
    return null;
  }

  public getSrvOrderState(refId: string): CubiqTS.OrderState {
    return this.getter.getSrvOrderState(refId);
  }

  /**********************************************************************/
  /* MKT STRATEGY API */

  public isMKTAllowedForStrategyLeg(refId: string): boolean {
    // node exists AND (is a single node OR (doesn't have a sibling AND doesn't have children))
    return (
      this.getter.has(refId) &&
      (!this.getter.hasStrategy(refId) || // its a lonely root
        (!this.getter.hasSiblings(refId) && !this.getter.hasChildren(refId)))
    ); // its end node/leg
  }

  public doesStrategyHaveMKTLeg(refId: string): boolean {
    return this.getter.doesStrategyHaveMKTLeg(refId);
  }

  public updateActionsAndNotificationsPanelsForMKTOrder(
    refId: string,
    updateStrategyButtons: boolean = true,
  ): void {
    // local node - strategy buttons
    if (typeof updateStrategyButtons === "boolean" && updateStrategyButtons) {
      this.coordinator.cmdUpdateStrategyButtonsLockingWithMKTCheck(refId);
    }
    // all nodes
    if (this.getter.doesStrategyHaveMKTLeg(refId)) {
      this.coordinator.cmdUpdateActionsSaveBtnAvailability(refId, true);
    } else {
      this.coordinator.cmdUpdateActionsSaveBtnAvailability(refId, false);
    }
  }

  public clearOrderTypeOnMKTStrategyLegs(refId: string): void {
    this.oeLogger.outputDevLogToConsole(`Invoked clearOrderTypeOnMKTStrategyLegs(${refId})`);
    this.operations.clearOrderTypeOnStrategyNodesOfType(refId, CubiqTS.RequestOrderType.MARKET);
    this.updateStrategy(refId);
  }

  public clearSuspendOnMKTStrategyLegs(refId: string): void {
    this.operations.clearSuspendOnStrategyNodesOfType(refId, CubiqTS.RequestOrderType.MARKET);
  }

  public resetToRootCcyPairOnMKTStrategyLegs(refId: string): void {
    this.operations.resetCcyPairToRootOnStrategyNodesOfType(refId, CubiqTS.RequestOrderType.MARKET);
  }

  /**********************************************************************/
  // test actions
  public testActionOrderStateDisplay(refId: string, displayState: OENDisplayState): void {
    if (this.getter.has(refId)) {
      let command = this.utility.transformDisplayStateToCmd(displayState);
      if (command != null) {
        this.coordinator.cmdChangeDisplayState(refId, command);
      }
    }
  }

  public getParentForStrategy(refId: string): string {
    let parent: string = "";
    if (this.getter.hasStrategy(refId)) {
      parent = this.getter.get(refId).strategy.parentRefId;
    }
    return parent;
  }

  public isTargetRefIdPartOfActiveStrategySet(activeRefId: string, targetRefId: string): boolean {
    if (this.getter.has(activeRefId) && this.getter.hasStrategy(activeRefId)) {
      let strategyRefIdSet = this.getter.getStrategyNodesListRoot(activeRefId);
      if (Array.isArray(strategyRefIdSet) && strategyRefIdSet.length > 0) {
        return strategyRefIdSet.includes(targetRefId);
      }
    }
    return false;
  }

  /**********************************************************************/
  /* API for Rate subscription  */
  public rateSubscribe(refId: string, entryType: OENEntryType, ccyPair: string): Subject<any> {
    let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
    return this.rateManager.subscribe(rootRefId, entryType, ccyPair);
  }

  public rateUnsubscribe(rootRefId: string, entryType: OENEntryType, ccyPair: string): void {
    if (this.getter.has(rootRefId)) {
      // when model is deleted, can't use getRootIdForNodeWithRefId() method
      rootRefId = this.getter.getRootIdForNodeWithRefId(rootRefId);
    }
    this.rateManager.unsubscribe(rootRefId, entryType, ccyPair);
  }

  public takeRateSubscription(
    refId: string,
    previousRefId: string,
    entryType: OENEntryType,
    ccyPair: string,
  ): void {
    this.rateManager.takeRateSubscription(refId, previousRefId, entryType, ccyPair);
  }

  /* public canCancelRateSubscription(rateSubscription: RateSubscription): boolean{
		return this.rateManager.canCancelRateSubscription(rateSubscription);
	} */

  /**********************************************************************/
  /* UTILITY Methods  */

  public updateStrategy(refId: string, updateAllNodes: boolean = false): void {
    if (this.getter.hasStrategy(refId)) {
      setTimeout(() => {
        this.coordinator.cmdUpdateStrategyLayout(refId, updateAllNodes);
      }, 100);
    }
  }

  protected destroyListOfOrderEntryStrategyInstances(refIds: Array<string>): void {
    /* if(refIds != null && Array.isArray(refIds) && refIds.length > 0){
			refIds.forEach((nodeRefId)=>{
				this.destroyOrderEntryInstance(nodeRefId);
			});
		} */
    this.actions.destroyInstancesList(refIds);
  }

  public transformCmdToDisplayState(cmd: OENCommand): OENDisplayState {
    return this.utility.transformCmdToDisplayState(cmd);
  }

  public saveClientPanelActiveTabs(clActiveTab: string, accActiveTab) {
    this.clientPanelActiveTabs.clientActiveTab = clActiveTab;
    this.clientPanelActiveTabs.accountActiveTab = accActiveTab;

    if (this.clientPanelActiveTabs != null) {
      let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
      profileSettings.push(
        this.userProfileService.createProfileSettingObject(
          ProfileSettingKeys.userOrderEntryActiveTabsSettings,
          JSON.stringify(this.clientPanelActiveTabs),
        ),
      );
      this._profileService
        .updateProfile(profileSettings, false)
        .then((value: any) => {
          this._activeUserService.getActiveUserData().then((userData: ActiveUser) => {
            if (userData.profileSettings != null) {
              userData.profileSettings.set(
                ProfileSettingKeys.userOrderEntryActiveTabsSettings,
                JSON.stringify(this.clientPanelActiveTabs),
              );
              this._activeUserService.setActiveUserData(userData);
            }
          });
        })
        .catch((error: any) => {
          logger.out.error(
            `ERROR: oeAPI.saveClientPanelActiveTabs(): Errors while saving client panel active tabs.`,
          );
        });
    }
  }

  /**********************************************************************/
  /* Launch Order entry */

  /**
   * This method is used to duplicate an order from modify window, when the user clicks "copy..." button
   * No external loading is needed in _oeScreen.glInit(), since the loading is handled internally, unlike invoking duplicate from the menu.
   * @param refId
   */
  public launchDuplicateOrderPopoutForModify(refId: string, entryType: OENEntryType) {
    this.doOrderEntryPopupForRefId(refId, refId, entryType, OENOperationMode.DUPLICATE, false);
  }

  public launchNewOrderPopout(
    refId: string,
    entryType: OENEntryType,
    loadDefaults: boolean = true,
    mode: OENOperationMode = OENOperationMode.NEW,
  ) {
    this.doOrderEntryPopupForRefId(
      refId,
      refId,
      entryType,
      OENOperationMode.NEW,
      null,
      loadDefaults,
    );
  }

  public doOrderEntryPopupForOrderId(
    //oeAPI: OEApiService,
    orderId: string,
    //entryType: OENEntryType, // not used
    mode: OENOperationMode,
    extLoad: boolean = null,
    loadDefaults: boolean = null,
    scratchpad: boolean = null,
  ): Promise<void> {
    // We might have a window up in a terminal state - just in case, we close
    const refId = this.getter.findRefIdForOrderId(orderId);
    if (refId) {
      // We already have a ticket open - close it if:
      // 1. we're not monitoring and we're trying to open the ticket in a different mode
      // 2. we're not monitoring ansd display stqate is terminal (i.e. old window with just Close button)
      // 3. if we've otherwise lost visibility
      if (
        (mode !== OENOperationMode.DUPLICATE &&
          !this.getter.supportsMonitoringExecution(refId) &&
          (mode !== this.getter.getOperationMode(refId) ||
            this.getter.isOrderDisplayStateTerminal(refId))) ||
        this.getter.getDisplayState(refId) === OENDisplayState.SHOW_LOST_HIDDEN
      ) {
        this.coordinator.cmdCloseOrderEntryScreen(refId);
      }
    }

    return this.actions.load(orderId, mode).then((response: OELoadingResponseModel) => {
      // if (displayedId) {
      //   this.coordinator.cmdActivateStrategyLegRefListener(displayedId);
      // }
      switch (response.outcome) {
        case OENLoadingOutcome.WARNING_CONTINUE:
          logger.out.warn(
            `WARN & CONTINUE: Order Entry ${OENOperationMode[mode]} LOAD: %O`,
            response.message,
          );
        // no break here
        case OENLoadingOutcome.SUCCESS: {
          if (response.refId && response.rootRefId && response.entryType != null) {
            this.doOrderEntryPopupForRefId(
              response.refId,
              response.rootRefId,
              response.entryType,
              mode,
              extLoad,
              loadDefaults,
              scratchpad,
            );
          } else {
            logger.out.error(
              `ERROR: Order Entry ${OENOperationMode[mode]} LOAD: Invalid IDs in response => refId: ${response.refId} | rootRefId: ${response.rootRefId} | entryType: ${response.entryType}`,
            );
          }
          break;
        }
        case OENLoadingOutcome.WARNING_BREAK: {
          logger.out.warn(
            `WARN & EXIT: Order Entry ${OENOperationMode[mode]} LOAD: %O`,
            response.message,
          );
          break;
        }
        case OENLoadingOutcome.ERROR: {
          logger.out.error(
            `ERROR: Order Entry ${OENOperationMode[mode]} LOAD: %O`,
            response.message,
          );
          break;
        }
        default: {
          logger.out.error(
            `ERROR: Order Entry ${OENOperationMode[mode]} LOAD: Invalid outcome in response: %O`,
            response,
          );
          break;
        }
      }
    });
  }

  public doOrderTicketPopup(orderId: string, mode: OENOperationMode) {
    let refId = this.getter.findRefIdForOrderId(orderId);
    let rootId = refId ? this.getter.getRootRefIdForStrategyRefId(refId) : null;
    let displayedId = refId ? this.getter.getActivelyDisplayedRefId(refId) : rootId;
    this.doOrderEntryPopupForOrderId(orderId, mode).then(() => {
      if (!refId) {
        refId = this.getter.findRefIdForOrderId(orderId);
        rootId = refId ? this.getter.getRootRefIdForStrategyRefId(refId) : null;
        displayedId = refId;
      }
      this.coordinator.cmdMonitorExecution(rootId);
      this.coordinator.cmdActivateStrategyLegRefListener(displayedId);
    });
  }

  /**
   * Refresh the contents of the strategy wicontaining the speicifed orderId/refId. THis is normally
   * only called after a rollback where the UI no longer has any idea of what needs to be displayed in the ticket
   *
   * @param orderId
   * @param refId
   * @returns
   */
  public doOrderTicketRefresh(orderId: string, refId: string) {
    // Save current display state such as displayed leg
    // DO normal load of strategy, locking everything
    // Once everything is loaded, reinstate any old view state and set the order into montirong state
    // const initEntryState = this.getEntryStateForOrderId(orderId);
    // this.coordinator.cmdLockInterfaceControls();
    const mode = OENOperationMode.MONITOR_EXECUTION;

    if (!this.getter.has(refId)) {
      logger.out.warn(`oeapi-s - Aborting refresh, refId ${refId} not found`);
      return Promise.resolve(true);
    }

    const rootId = refId ? this.getter.getRootRefIdForStrategyRefId(refId) : null;
    let displayedId = refId ? this.getter.getActivelyDisplayedRefId(refId) : null;

    if (this.activeOrderRefreshMap.has(rootId)) {
      // Prevent rollback of multiple orders in same strategy causing multiple refreshes
      logger.out.info(
        `oea-s - ignoring reload for all order strategy data for order Id ${orderId}, ref = ${refId}`,
      );
      return;
    }
    logger.out.info(
      `oea-s - Reloading all order strategy data for root Id ${rootId}, ref = ${refId}`,
    );
    this.activeOrderRefreshMap.add(rootId);
    this.coordinator.cmdChangeDisplayState(refId, OENCommand.STATE_ORDER_PROCESSING);

    return this.actions.load(orderId, mode).then(
      (response: OELoadingResponseModel) => {
        logger.out.info(
          `oea-s - processed reload for all order strategy data for order Id ${orderId}, ref = ${refId}`,
        );
        if (!this.getter.has(displayedId)) {
          // On refresh, the previously displayed leg may have been removed dure to a rollback
          displayedId = rootId;
        }
        switch (response.outcome) {
          case OENLoadingOutcome.WARNING_CONTINUE:
          case OENLoadingOutcome.SUCCESS:
            if (response.refId && response.rootRefId && response.entryType != null) {
              this.coordinator.cmdModifyMonitoredOrder(refId, mode);
              this.coordinator.cmdMonitorExecution(rootId);
              this.coordinator.cmdActivateStrategyLegRefListener(displayedId);
              setTimeout(() => this.coordinator.cmdRefreshStrategyLayout(rootId), 100);
            } else {
              logger.out.error(`oea-s - Cannot find ref or entryType`);
            }
            break;
          default:
            break;
        }
        if (response.outcome !== OENLoadingOutcome.SUCCESS) {
          logger.out.warn(
            `oea-s - load warning: Order Entry ${OENOperationMode[mode]} outcome = {OENLoadingOutcome[response.outcome]}: ${response.message}`,
          );
        }
        this.activeOrderRefreshMap.delete(rootId);
      },
      (reason) => {
        logger.out.warn(`Error loading order ${rootId} - reason ${reason}`);
        this.activeOrderRefreshMap.delete(rootId);
      },
    );
  }

  public doOrderEntryPopupForRefId(
    refId: string,
    rootRefId: string,
    entryType: OENEntryType,
    mode: OENOperationMode,
    extLoad: boolean = null,
    loadDefaults: boolean = null,
    scratchpad: boolean = null,
  ) {
    if (this.screenRegistry.has(refId)) {
      // We already have a window open for this order
      if (this.getter.supportsMonitoringExecution(refId)) {
        logger.out.info("oe-api: Not creating entry window as one should already exist");
        this.coordinator.cmdModifyMonitoredOrder(refId, mode);
        return;
      }
    }

    let config: GLPopoutConfiguration = this.settings.getWindowPopoutConfiguration();
    config.data = {
      refId: refId,
      rootRefId: rootRefId,
      entryType: entryType,
      mode: mode,
    };

    if (typeof extLoad === "boolean") {
      config.data.extLoad = extLoad;
    }
    if (typeof loadDefaults === "boolean") {
      config.data.loadDefaults = loadDefaults;
    }
    if (typeof scratchpad === "boolean") {
      config.data.scratchpad = scratchpad;
    }
    let title: string = this.utility.determineScreenTitle(entryType, mode);

    let type: DashboardItemType = DashboardItemType.NewOrder;
    switch (mode) {
      case OENOperationMode.NEW:
        type = DashboardItemType.NewOrder;
        break;
      case OENOperationMode.MODIFY:
        type = DashboardItemType.OrderModify;
        break;
      case OENOperationMode.MODIFY_INFO:
        type = DashboardItemType.OrderModifyInfo;
        break;
      case OENOperationMode.MONITOR_EXECUTION:
        type = DashboardItemType.OrderModify;
        break;
      case OENOperationMode.AMEND_MARKUP:
        type = DashboardItemType.OrderAmendMarkup;
        break;
      case OENOperationMode.DUPLICATE:
        type = DashboardItemType.NewOrder;
        break;
    }

    this.layoutService.launchFromDashboard(
      title,
      type,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  /**********************************************************************/
  /* SCREEN REGISTRY  */

  public screenRegister(refId: string): void {
    this.screenRegistry.register(refId);
  }

  public screenUnregister(refId: string): void {
    this.screenRegistry.unregister(refId);
  }

  public screenPreregister(originalRefId: string, newRefId: string): void {
    this.screenRegistry.preregister(originalRefId, newRefId);
  }

  public screenHas(refId: string): void {
    this.screenRegistry.has(refId);
  }

  public screenUpdateFull(refId: string): void {
    this.screenRegistry.updateFull(refId);
  }

  public screenUpdateDisplay(refId: string, displayState: OENDisplayState): void {
    this.screenRegistry.updateDisplay(refId, displayState);
  }
  /**********************************************************************/
  /* DEV TESTING  */
  public testValidateAlgoParams(refId: string): void {
    let errors = new Array<OEInputModel>();
    let dataValid = this.validator.validateOrderData(refId, errors); // ,this.getter.isOperationModeModify(refId)

    if (!dataValid) {
      this.coordinator.cmdInvalidInput(refId, errors);
    }
  }

  /**********************************************************************/
  /* DEBUG  */

  public getDebugBtnState(): boolean {
    return this.settings.enableDebugBtn;
  }

  public overwriteClientDatasetMode(value: boolean): void {
    this.settings.setClientDatasetMode(value);
  }

  public isClientDatasetModeStrict(): boolean {
    return this.settings.strictClientDataset;
  }

  // LOGGING - GENERIC
  public setExtendedWorkerLoggingState(logState: boolean): void {
    this.logSettings.setExtendedLoggingState(logState);
    let extendedLogging = this.logSettings.getExtendedLoggingState();
    let modelDumpLogging = this.logSettings.getDumpModelLoggingState();
    this.coordinator.cmdUpdateLoggingStateDisplay(
      OENExtendedLoggingType.GENERIC,
      extendedLogging,
      modelDumpLogging,
    );
    this.workerActions.setExtendedWorkerLoggingState(extendedLogging);
  }
  public getExtendedLoggingState(): boolean {
    return this.logSettings.getExtendedLoggingState();
  }

  public setDumpModelLoggingState(logState: boolean): boolean {
    let currentDumpModelLoggingState = this.logSettings.getDumpModelLoggingState();
    let newDumpModelLoggingState = this.logSettings.setDumpModelLoggingState(logState);
    if (currentDumpModelLoggingState !== newDumpModelLoggingState) {
      this.coordinator.cmdUpdateLoggingStateDisplay(
        OENExtendedLoggingType.GENERIC,
        this.logSettings.getExtendedLoggingState(),
        newDumpModelLoggingState,
      );
    }
    return newDumpModelLoggingState;
  }

  public getDumpModelLoggingState(): boolean {
    return this.logSettings.getDumpModelLoggingState();
  }

  // LOGGING - RATE
  public setRateExtendedWorkerLoggingState(logState: boolean): void {
    this.logSettings.setRateExtendedLoggingState(logState);
    let extendedLogging_rate = this.logSettings.getRateExtendedLoggingState();
    let modelDumpLogging_rate = this.logSettings.getRateDumpModelLoggingState();
    this.coordinator.cmdUpdateLoggingStateDisplay(
      OENExtendedLoggingType.RATE,
      extendedLogging_rate,
      modelDumpLogging_rate,
    );
    //this.workerActions.setExtendedWorkerLoggingState(extendedLogging_rate);
  }
  public getRateExtendedWorkerLoggingState(): boolean {
    return this.logSettings.getRateExtendedLoggingState();
  }

  public setRateDumpModelLoggingState(logState: boolean): boolean {
    let currentDumpModelLoggingState = this.logSettings.getRateDumpModelLoggingState();
    let newDumpModelLoggingState = this.logSettings.setRateDumpModelLoggingState(logState);
    if (currentDumpModelLoggingState !== newDumpModelLoggingState) {
      this.coordinator.cmdUpdateLoggingStateDisplay(
        OENExtendedLoggingType.RATE,
        this.logSettings.getExtendedLoggingState(),
        newDumpModelLoggingState,
      );
    }
    return newDumpModelLoggingState;
  }

  public getRateDumpModelLoggingState(): boolean {
    return this.logSettings.getRateDumpModelLoggingState();
  }

  /* ******************************************** */
  /* LOGOUT */
  ///// ADDED AS PART OF OMS-28059
  //USED TO DETERMINE IF ENTRY MODEL HAVE ORDERS IN DI STATE

  public hasOrdersInDI(): boolean {
    return this.getter.hasOrdersInDI();
  }

  /**
   * Get delta value from the referenced model
   * @param refId the ref id of the model
   * @returns the delta in the model if the model is found or null
   */
  public getGammaDelta(refId: string): string {
    let model = this.getter.get(refId);
    return model != null ? model.delta : null;
  }

  /**
   * Returns true if the order ticket is monitoring the execution of an order after updates
   * @param refId
   * @returns
   */
  public monitoringExecution(refId: string): boolean {
    return this.getter.monitoringExecution(refId);
  }

  /**
   * Returns true if the order ticket supports continued execution monitoring after completion
   * @param refId
   * @returns
   */
  public supportsMonitoringExecution(refId: string): boolean {
    return this.getter.supportsMonitoringExecution(refId);
  }

  /**
   * Resolve the output of a server call to an array of error messages resulting from one or more ErrorsImpl. The server results should be
   * a Promise containing an array of values which are either null, or ErrorImpl instances
   * Acceptance, for example, will split all calls into batches and return an Error for each batch
   * @param operation
   * @returns
   */
  private reduceServerErrors(operation: Promise<any[]>): Promise<CubiqTS.ErrorsImpl[]> {
    return operation.then((results) => {
      let retErrors = [];
      if (Array.isArray(results) && results.length > 0) {
        results.forEach((result) => {
          if (result instanceof CubiqTS.ErrorsImpl && result.errorMessages?.length > 0) {
            retErrors.push(result);
          }
        });
      } else {
        logger.out.warn(`oeapi-s - unknown server results ${results}`);
      }
      return retErrors;
    });
  }
}
